// src/composables/useSecurityRefresh.js
import { ref, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { getProfile } from "@/services/userService";
import { ADMIN_LEVELS } from "@/constants/adminLevels";
import { logger } from "@/utils/logger";

/**
 * Composable para gerenciar refresh de segurança do perfil
 *
 * Estratégia de refresh:
 * 1. Após login inicial - já feito no Login.vue
 * 2. Periodicamente (a cada 5 minutos) - para capturar mudanças de permissão
 * 3. Antes de operações sensíveis - usando refreshProfileNow()
 *
 * O backend tem cache de 24h, mas faz refresh automático quando há alteração nos dados.
 * Portanto, é seguro chamar com frequência.
 */

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutos
const MIN_REFRESH_INTERVAL = 30 * 1000; // Mínimo de 30 segundos entre refreshes

export function useSecurityRefresh() {
  const auth = useAuthStore();

  const isRefreshing = ref(false);
  const lastRefreshError = ref(null);
  let intervalId = null;

  /**
   * Atualiza o perfil do backend
   * @param {boolean} useCache - Se deve usar cache (default: false para forçar refresh)
   * @returns {Promise<Object|null>} O perfil atualizado ou null em caso de erro
   */
  async function refreshProfileNow(useCache = false) {
    // Evita refresh se não estiver logado
    if (!auth.isLoggedIn) {
      logger.debug("Usuário não logado, ignorando refresh");
      return null;
    }

    // Evita refreshes muito frequentes
    const now = Date.now();
    if (
      auth.lastProfileRefresh &&
      now - auth.lastProfileRefresh < MIN_REFRESH_INTERVAL
    ) {
      logger.debug("Refresh muito recente, ignorando");
      return auth.profile;
    }

    isRefreshing.value = true;
    lastRefreshError.value = null;

    try {
      logger.debug("Atualizando perfil do backend...");
      const profileData = await getProfile(useCache);

      // O endpoint /Profiles/me/full NÃO retorna admin_level
      // O admin_level vem apenas no login, então mantemos o valor atual
      // Apenas atualizamos os dados do perfil
      const currentAdminLevel = auth.adminLevelValue;
      const currentAdminLevelName = auth.adminLevelDisplayName;

      // Atualiza o store mantendo o admin level atual
      auth.updateProfile({
        profile: profileData,
        adminLevel: currentAdminLevel,
        adminLevelName: currentAdminLevelName,
      });

      logger.debug("Perfil atualizado", {
        adminLevel: currentAdminLevel,
        adminLevelName: currentAdminLevelName,
      });

      return profileData;
    } catch (error) {
      logger.error("Erro ao atualizar perfil", error);
      lastRefreshError.value = error.message;

      // NÃO fazer logout em caso de erro - apenas logar
      // O interceptor do api.js já trata 401 quando necessário
      return null;
    } finally {
      isRefreshing.value = false;
    }
  }

  /**
   * Inicia o refresh periódico
   */
  function startPeriodicRefresh() {
    if (intervalId) {
      clearInterval(intervalId);
    }

    logger.debug("Iniciando refresh periódico");

    intervalId = setInterval(() => {
      refreshProfileNow(true); // Usa cache por padrão no refresh periódico
    }, REFRESH_INTERVAL);
  }

  /**
   * Para o refresh periódico
   */
  function stopPeriodicRefresh() {
    if (intervalId) {
      logger.debug("Parando refresh periódico");
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  /**
   * Verifica se o usuário tem permissão para uma operação sensível
   * Faz refresh do perfil antes de verificar
   * @param {number} requiredLevel - Nível mínimo necessário
   * @returns {Promise<boolean>} Se tem permissão
   */
  async function checkPermissionForSensitiveOperation(requiredLevel) {
    logger.debug("Verificando permissão para operação sensível", {
      requiredLevel,
    });

    // Força refresh sem cache para operações sensíveis
    await refreshProfileNow(false);

    const hasPermission = auth.adminLevelValue >= requiredLevel;

    if (!hasPermission) {
      logger.warn("Permissão negada para operação sensível", {
        userLevel: auth.adminLevelValue,
        requiredLevel,
      });
    }

    return hasPermission;
  }

  // Lifecycle hooks para componentes
  onMounted(() => {
    if (auth.isLoggedIn) {
      startPeriodicRefresh();
    }
  });

  onUnmounted(() => {
    stopPeriodicRefresh();
  });

  return {
    isRefreshing,
    lastRefreshError,
    refreshProfileNow,
    startPeriodicRefresh,
    stopPeriodicRefresh,
    checkPermissionForSensitiveOperation,
  };
}

/**
 * Hook para verificar permissão antes de operações sensíveis
 * Uso: const canProceed = await verifySensitiveOperation(ADMIN_LEVELS.SUPER);
 */
export async function verifySensitiveOperation(requiredLevel) {
  const auth = useAuthStore();

  // Se não está logado, não tem permissão
  if (!auth.isLoggedIn) return false;

  try {
    // Força refresh do perfil para validar se ainda está ativo
    const profileData = await getProfile(false);

    // IMPORTANTE: Manter o admin_level atual - o endpoint /Profiles/me/full NÃO retorna admin_level
    // O admin_level vem apenas do login e é armazenado no store
    const currentAdminLevel = auth.adminLevel ?? ADMIN_LEVELS.DISABLED;

    // Atualiza o store mantendo o admin level atual
    auth.updateProfile({
      profile: profileData,
      adminLevel: currentAdminLevel,
      adminLevelName: auth.adminLevelName ?? "",
    });

    return currentAdminLevel >= requiredLevel;
  } catch (error) {
    logger.error("verifySensitiveOperation: Erro", error);
    return false;
  }
}
